import { FunctionComponent, useEffect, useState } from 'react'
import DashboardLayout from '../../components/layout/layout'
import { Alert, Box, Button, Grid, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { StyledTextField } from '../../components/ui/formInput'
import { useGetChannelPartnerOEntryHeaderAddCart, useGetChannelPartnerOEntryHeaderLeadTime, useGetChannelPartnerOEntryHeaderProductDetails, useGetChannelPartnerOEntryHeaderSetPoint } from '../../hooks/api/oms'
import StorageService from '../../services/storageService'
import moment from 'moment';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  partNumber: ValidateField;
  setPoint: ValidateField;
  description: ValidateField;
  listPrice: ValidateField;
  leadTime: ValidateField;
  quantity: ValidateField;
  requestedShipDate: ValidateField;
  comment: ValidateField;
};

const OnlineOrderEntryAddProduct: FunctionComponent = () => {
  const navigate = useNavigate();
  const { productNumber, productQuantity } = useParams();
  const { data: productDetail } = useGetChannelPartnerOEntryHeaderProductDetails(productNumber || '', productQuantity || '');
  const [error, setError] = useState('');
  const [disabled, setDisabled] = useState<boolean>(false);
  const [requestedShipDate, setRequestedShipDate] = useState<string>('');
  const [isRequestedShipDateUserModified, setIsRequestedShipDateUserModified] = useState<boolean>(() => {
    // Check localStorage for persisted user modification flag
    const savedFlag = localStorage.getItem(`ship_date_modified_${productNumber || 'unknown'}`);
    return savedFlag === 'true';
  });
  const [comment, setComment] = useState<string>('');
  const [partNumber, setPartNumber] = useState<string>('');
  const [setPoint, setSetPoint] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [listPrice, setListPrice] = useState<string>('');
  const [leadTime, setLeadTime] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');

  const setPoints = StorageService.getOEntryHeaderSetPointValue();

  const oEntryHeader = StorageService.getOEntryHeader();
  const oEntryHeaderValue = StorageService.getOEntryHeaderValue();
  const shipVia = oEntryHeader?.shipViaOptions?.find((option: any) => option.fr_list === oEntryHeaderValue?.shipViaCarrier) || {};

  const {data: leadTimeResponse} = useGetChannelPartnerOEntryHeaderLeadTime(productDetail?.partNumber || null, setPoints?.partNumber || null);
  const {data: setPointData, isLoading: setPointLoading, error: setPointError} = useGetChannelPartnerOEntryHeaderSetPoint(productDetail?.partNumber || '');
  
  const { mutateAsync: addToCart } = useGetChannelPartnerOEntryHeaderAddCart();

  const nonEditable = true;

  useEffect(() => {
    if (productDetail) {
      setPartNumber(productDetail.partNumber || '');
      setDescription(productDetail.pt_desc1 || '');
      setQuantity(productDetail.quantity || '');
      // Only set date if user hasn't modified it
      if (!isRequestedShipDateUserModified) {
        formatDate(productDetail.requestedShipDate)
      }
      formatListPrice(productDetail.listPrice)
      formatLeadTime(productDetail.leadTime, productDetail.orderCapExceeded)
    }
  }, [productDetail, isRequestedShipDateUserModified]);

  useEffect(() => {
    if (setPoints && setPoints.partNumber) {
      setPartNumber(setPoints.partNumber);
      setDescription(setPoints.description);
      setSetPoint(setPoints.setPoint);
      //   formatListPrice(setPoints.listPrice);
      const listPriceValue = setPoints.listPrice;
      if (listPriceValue && !isNaN(Number(listPriceValue)) && listPriceValue !== '') {
        formatListPrice(listPriceValue);
      } else {
        setListPrice(listPriceValue);
      } 
      // else {
      //   // If no value, leave empty for placeholder
      //   setListPrice('');
      // }
    } 
  }, [setPoints]);

  useEffect(() => {
    if (leadTimeResponse && leadTimeResponse.leadTime) {
      // Check locally if current quantity exceeds order cap
      const currentQuantity = Number(quantity);
      const orderCap = leadTimeResponse.orderCap;
      const isOrderCapExceeded = orderCap && orderCap > 0 && currentQuantity > orderCap;
      
      formatLeadTime(leadTimeResponse.leadTime, isOrderCapExceeded)
      // Only set date if user hasn't modified it AND the current ship date is not blank
      // This preserves blank ship dates that were set due to order cap exceeded
      if (!isRequestedShipDateUserModified && requestedShipDate !== '') {
        formatDate(leadTimeResponse.requestedShipDate)
      }
    }
  }, [leadTimeResponse, isRequestedShipDateUserModified, requestedShipDate, quantity]);

  const formatDate = (date: string) => {
    // Only set the date if user hasn't manually modified it
    if (!isRequestedShipDateUserModified) {
      setRequestedShipDate(date ? moment(date, "MM-DD-YY").format("YYYY-MM-DD") : '');
    }
  }

  const formatListPrice = (price: string | null) => {
    // setListPrice(price ? `$${price}${Number.isInteger(price) ? '.00' : ''}` : '');
    // Handle 0 as a valid price (including decimal formats)
    if (price === '0' || price === '0.00' || Number(price) === 0) {
      setListPrice('$0.00');
    } else if (price) {
      // Convert to number first to handle decimal cases properly
      const numPrice = Number(price);
      if (numPrice === 0) {
        setListPrice('$0.00');
      } else {
        setListPrice(`$${price}${Number.isInteger(numPrice) ? '.00' : ''}`);
      }
    } else {
      setListPrice('');
    }
  }

  const formatLeadTime = (leadTime: string | null, orderCapExceeded?: boolean) => {
    // MODIFIED: When order cap is exceeded, show "Call Customer Care" for lead time
    // When order cap is not exceeded, show actual lead time from API
    if (orderCapExceeded) {
      // Show "Call Customer Care" when order cap exceeded
      setLeadTime('Call Customer Care :(800) 835-1060');
    } else {
      // Normal behavior: show actual lead time from API
      if (!leadTime || leadTime === '0' || parseInt(leadTime) === 0) {
        setLeadTime('Call Customer Care :(800) 835-1060');
      } else {
        setLeadTime(`${leadTime} Working Days`);
      }
    }
  }

  const formValidation: ValidateFieldsState = {
    requestedShipDate: { invalid: false, error: '' },
    comment: { invalid: false, error: '' },
    partNumber: { invalid: false, error: '' },
    setPoint: { invalid: false, error: '' },
    description: { invalid: false, error: '' },
    listPrice: { invalid: false, error: '' },
    leadTime: { invalid: false, error: '' },
    quantity: { invalid: false, error: '' }
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    // Requested Ship Date is not mandatory - validation removed
    errors.requestedShipDate.invalid = false;
    errors.requestedShipDate.error = '';

    if (!partNumber) {
      errors.partNumber.invalid = true;
      errors.partNumber.error = 'Part Number is required';
      valid = false;
    } else {
      errors.partNumber.invalid = false;
      errors.partNumber.error = '';
    }

    // if (!setPoint) {
    //   errors.setPoint.invalid = true;
    //   errors.setPoint.error = 'Set Point is required';
    //   valid = false;
    // } else {
    //   errors.setPoint.invalid = false;
    //   errors.setPoint.error = '';
    // }

    // New validation: Set Point is only required when status is "K"
    if (productDetail?.status === "K" && !setPoint) {
      errors.setPoint.invalid = true;
      errors.setPoint.error = 'Set Point is required';
      valid = false;
    } else {
      errors.setPoint.invalid = false;
      errors.setPoint.error = '';
    }

    if (!description) {
      errors.description.invalid = true;
      errors.description.error = 'Description is required';
      valid = false;
    } else {
      errors.description.invalid = false;
      errors.description.error = '';
    }

    if (!listPrice || listPrice.trim() === '') {
      errors.listPrice.invalid = true;
      errors.listPrice.error = 'List Price is required';
      valid = false;
    } else {
      errors.listPrice.invalid = false;
      errors.listPrice.error = '';
    }

    if (!leadTime) {
      errors.leadTime.invalid = true;
      errors.leadTime.error = 'Lead Time is required';
      valid = false;
    } else {
      errors.leadTime.invalid = false;
      errors.leadTime.error = '';
    }

    if (!quantity) {
      errors.quantity.invalid = true;
      errors.quantity.error = 'Quantity is required';
      valid = false;
    } else {
      errors.quantity.invalid = false;
      errors.quantity.error = '';
    }

    // Validate Comment character limit
    if (comment.length > 68) {
      errors.comment.invalid = true;
      errors.comment.error = 'Comment cannot exceed 68 characters';
      valid = false;
    } else {
      errors.comment.invalid = false;
      errors.comment.error = '';
    }

    setValidateFields(errors);
    return valid;
  }

  const handleNext = async () => {
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    setError('');
    setDisabled(true);
    const orderDetails = {
      partNumber: partNumber,
      setPoint: setPoint,
      description: description,
      listPrice: listPrice.replace('$', ''),
      leadTime: leadTime.replace(' Working Days', ''),
      quantity: quantity,
      requestedShipDate: requestedShipDate ? moment(requestedShipDate, "YYYY-MM-DD").format("MM/DD/YY") : "",
      comment: comment,
      shipVia: shipVia.fr_list || ''
    }
    try {
      const result = await addToCart({
        partNumber: orderDetails.partNumber,
        quantity: orderDetails.quantity,
        comment: orderDetails.comment,
        shipViaOverride: orderDetails.shipVia,
        requestedShipDate: orderDetails.requestedShipDate,
        requestedShipDateChange: isRequestedShipDateUserModified ? 1 : 0,
      })
      
      if (result?.status === 200) {
        // Store comment in localStorage as backup to ensure it's available in order page
        const cartComments = JSON.parse(localStorage.getItem('cart_comments') || '{}');
        cartComments[partNumber] = comment;
        localStorage.setItem('cart_comments', JSON.stringify(cartComments));
        
        // Store updated ship date in localStorage so order page can access it
        const cartShipDates = JSON.parse(localStorage.getItem('cart_ship_dates') || '{}');
        cartShipDates[partNumber] = requestedShipDate;
        localStorage.setItem('cart_ship_dates', JSON.stringify(cartShipDates));
        
        // Clear the modification flag as it's now saved in cart
        localStorage.removeItem(`ship_date_modified_${productNumber || 'unknown'}`);
        
        setDisabled(false);
        navigate(`/order-management-system/online-order-entry/order`);
      } else {
        setDisabled(false);
        // Handle validation errors from API
        if (result?.data?.errors && Array.isArray(result.data.errors) && result.data.errors.length > 0) {
          // Display validation errors (like ship date validation)
          setError(result.data.errors.join(', '));
        } else {
          // Check for 'error' field first (from cart add API), then fallback to 'message'
          let errorMessage = result?.data?.error || result?.data?.message || 'An error occurred during add to cart. Please try again.';
          
          // COMMENTED OUT: Order capacity error handling - matches production behavior (AddProduct2.asp)
          // Production system allows quantities above order cap without showing error messages
          // Order cap exceeded items are allowed to proceed without user notification
          /*
          // If it's an order capacity error, include the order capacity value
          if (result?.data?.error === 'Quantity exceeds order capacity' && result?.data?.orderCap) {
            errorMessage = `Part ${partNumber || productNumber}: Quantity exceeds order capacity. Maximum allowed: ${result.data.orderCap}`;
          }
          */
          
          setError(errorMessage);
        }
        setTimeout(() => {
          setError('');
        }, 5000); // Show error for 5 seconds
      }
    } catch (err: any) {
      setDisabled(false);
      // Handle validation errors from API response
      if (err?.response?.data?.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
        setError(err.response.data.errors.join(', '));
      } else if (err?.response?.data?.error) {
        let errorMessage = err.response.data.error;
        
        // If it's an order capacity error, include the order capacity value
        if (err.response.data.error === 'Quantity exceeds order capacity' && err.response.data.orderCap) {
          errorMessage = `Part ${partNumber || productNumber}: Quantity exceeds order capacity. Maximum allowed: ${err.response.data.orderCap}`;
        }
        
        setError(errorMessage);
      } else if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during add to cart. Please try again.');
      }
      setTimeout(() => {
        setError('');
      }, 5000); // Show error for 5 seconds
    }
  };

  const handleAddSetPoint = () => {
    navigate('/order-management-system/online-order-entry/add-product/set-point/' + partNumber + '/' + quantity);
  };

  return (
    <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Online Order Entry"} breadcrumbs={'Add products'} activeMenu={'oms'}>
      <Box sx={{ backgroundColor: '#f5f5f5', border: '1px solid #e5e5e5', pb: 2 }}>
        <Grid container sx={{backgroundColor: '#c3183c', color: '#ffffff', padding: '10px 20px'}}>
          <Grid size={8}>
            <Typography variant="h6">Add Product To Order</Typography>
          </Grid>
          <Grid size={4} sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center'}}>
            <Typography sx={{ fontSize: '14px', mr: 2 }}>Order Information</Typography>
            <Typography sx={{ fontSize: '14px', borderBottom: '2px solid #e5496a', cursor: 'pointer' }} onClick={() => navigate('/order-management-system/online-order-entry/header')}>
              Edit
            </Typography>
          </Grid>
        </Grid>
        <Grid container sx={{padding: '10px 20px', mb: 2}} spacing={2}>
          <Grid size={3}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Part Number
            </Typography>
            <StyledTextField
              label="Part Number"
              value={partNumber}
              onChange={(e: any) => setPartNumber(e.target.value)}
              error={validateFields.partNumber.invalid}
              helperText={validateFields.partNumber.error}
              disabled={nonEditable}
              inputProps={{
                style: {
                  color: partNumber ? '#333333' : 'inherit',
                  fontWeight: partNumber ? '500' : 'inherit',
                  WebkitTextFillColor: partNumber ? '#333333' : 'inherit',
                }
              }}
            />
          </Grid>
          <Grid size={6}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Set Point {productDetail?.status === "K" && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            {/* Implement legacy ASP set point logic */}
            {(() => {
              // DEBUG: Log the values
              return null;
            })()}
            {productDetail?.pt_desc2 && productDetail.pt_desc2.trim() !== '' ? (
              // Case 1: Product has existing set point - show it
              <Typography variant="body1" sx={{ p: 1, border: '1px solid #ccc', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                ({productDetail.pt_desc2})
              </Typography>
            ) : setPointData?.data?.setPointRequired ? (
              // Case 2: No existing set point, but set points are available - show field + button
              <>
                <StyledTextField
                  label=""
                  placeholder={productDetail?.status === "K" ? "Set Point is required for this product" : "See only if specific set point is required"}
                  value={setPoint}
                  onChange={(e: any) => setSetPoint(e.target.value)}
                  error={validateFields.setPoint.invalid}
                  helperText={validateFields.setPoint.error}
                  disabled={nonEditable}
                  inputProps={{
                    style: {
                      color: setPoint ? '#333333' : 'inherit',
                      fontWeight: setPoint ? '500' : 'inherit',
                      WebkitTextFillColor: setPoint ? '#333333' : 'inherit',
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-input::placeholder': {
                      color: '#666666',
                      opacity: 1,
                    },
                    '& .MuiInputBase-input::-webkit-input-placeholder': {
                      color: '#666666',
                      opacity: 1,
                    },
                    '& .MuiInputBase-input::-moz-placeholder': {
                      color: '#666666',
                      opacity: 1,
                    }
                  }}
                />
              </>
            ) : (
              // Case 3: No existing set point, no set points available - show "N/A"
              <Typography variant="body1" sx={{ p: 1, border: '1px solid #ccc', borderRadius: 1, backgroundColor: '#f9f9f9', color: '#666' }}>
                N/A
              </Typography>
            )}
          </Grid>
          <Grid size={3} sx={{ display: 'flex', alignItems: 'flex-end' }}>
            {/* ADD SET POINT button - only show when setPointRequired is true */}
            {!productDetail?.pt_desc2 && setPointData?.data?.setPointRequired && (
              <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', height: '40px', minWidth: '110px', marginRight: '25px' }} onClick={handleAddSetPoint}>
                ADD SET POINT
              </Button>
            )}
          </Grid>
          <Grid size={3}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Description
            </Typography>
            <StyledTextField
              label="Description"
              value={description}
              onChange={(e: any) => setDescription(e.target.value)}
              error={validateFields.description.invalid}
              helperText={validateFields.description.error}
              disabled={nonEditable}
              inputProps={{
                style: {
                  color: description ? '#333333' : 'inherit',
                  fontWeight: description ? '500' : 'inherit',
                  WebkitTextFillColor: description ? '#333333' : 'inherit',
                }
              }}
            />
          </Grid>
          <Grid size={3}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              List Price
            </Typography>
            <StyledTextField
              label="List Price"
              value={listPrice}
              onChange={(e: any) => setListPrice(e.target.value)}
              error={validateFields.listPrice.invalid}
              helperText={validateFields.listPrice.error}
              disabled={nonEditable}
              inputProps={{
                style: {
                  color: listPrice ? '#333333' : 'inherit',
                  fontWeight: listPrice ? '500' : 'inherit',
                  WebkitTextFillColor: listPrice ? '#333333' : 'inherit',
                }
              }}
            />
          </Grid>
          <Grid size={3}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Lead Time
            </Typography>
            <StyledTextField
              label="Lead Time"
              value={leadTime}
              onChange={(e: any) => setLeadTime(e.target.value)}
              error={validateFields.leadTime.invalid}
              helperText={validateFields.leadTime.error}
              disabled={nonEditable}
              inputProps={{
                style: {
                  color: leadTime ? '#333333' : 'inherit',
                  fontWeight: leadTime ? '500' : 'inherit',
                  WebkitTextFillColor: leadTime ? '#333333' : 'inherit',
                }
              }}
            />
          </Grid>
          <Grid size={3}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Quantity
            </Typography>
            <StyledTextField
              label="Quantity"
              value={quantity}
              onChange={(e: any) => setQuantity(e.target.value)}
              error={validateFields.quantity.invalid}
              helperText={validateFields.quantity.error}
              disabled={nonEditable}
              inputProps={{
                style: {
                  color: quantity ? '#333333' : 'inherit',
                  fontWeight: quantity ? '500' : 'inherit',
                  WebkitTextFillColor: quantity ? '#333333' : 'inherit',
                }
              }}
            />
          </Grid>
        </Grid>
        <Grid container sx={{backgroundColor: '#c3183c', color: '#ffffff', padding: '10px 20px'}}>
          <Grid size={8}>
            <Typography variant="h6">Optional Settings</Typography>
          </Grid>
        </Grid>
        <Grid container sx={{padding: '10px 20px'}} spacing={2}>
          <Grid size={3}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Requested Ship Date
            </Typography>
            <StyledTextField
              label=""
              value={requestedShipDate}
              type={'date'}
              onChange={(e: any) => {
                setRequestedShipDate(e.target.value);
                setIsRequestedShipDateUserModified(true);
                // Persist user modification flag to localStorage
                localStorage.setItem(`ship_date_modified_${productNumber || 'unknown'}`, 'true');
              }}
              error={validateFields.requestedShipDate.invalid}
              helperText={validateFields.requestedShipDate.error}
              inputProps={{
                style: {
                  color: requestedShipDate ? '#333333' : 'inherit',
                  fontWeight: requestedShipDate ? '500' : 'inherit',
                }
              }}
            />
          </Grid>
          <Grid size={9}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Comment
            </Typography>
            <StyledTextField
              label="Comment"
              value={comment.toUpperCase()}
              onChange={(e: any) => setComment(e.target.value.toUpperCase())}
              error={validateFields.comment.invalid}
              helperText={validateFields.comment.error}
              inputProps={{
                maxLength: 68,
                style: {
                  color: comment ? '#333333' : 'inherit',
                  fontWeight: comment ? '500' : 'inherit',
                  WebkitTextFillColor: comment ? '#333333' : 'inherit',
                }
              }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              {comment.length}/68 characters
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Grid size={12} sx={{ mt: 4 }}>
        <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px', marginRight: '25px' }} onClick={handleNext}>
          ADD TO ORDER
        </Button>
        <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px' }} onClick={() => navigate('/order-management-system/online-order-entry/order')}>
          DO NOT ADD
        </Button>
      </Grid>
    </DashboardLayout>
  )
}

export default OnlineOrderEntryAddProduct